"use client";

import { Button } from "@/components/ui/button";
import {
	Field,
	FieldError,
	FieldGroup,
	FieldLabel,
} from "@/components/ui/field";
import { signIn } from "@/lib/auth/auth";
import { SITE_NAME } from "@/lib/constants";
import { EmailSchema, PasswordSchema, cn } from "@/lib/utils";
import { valibotResolver } from "@hookform/resolvers/valibot";
import { KeyIcon, MailIcon, UserIcon } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import * as v from "valibot";
import {
	Card,
	CardContent,
	CardFooter,
	CardHeader,
	CardTitle,
} from "../ui/card";
import ZeekMapsIcon from "../ui/icons/ZeekMapsIcon";
import {
	InputGroup,
	InputGroupAddon,
	InputGroupInput,
} from "../ui/input-group";

interface SignInFormParams {
	email?: string;
	className?: string;
}

const schema = v.pipe(
	v.object({
		email: EmailSchema,
		password: PasswordSchema,
	}),
);

export default function SignInForm({ email, className }: SignInFormParams) {
	const [isLoading, setIsLoading] = useState(false);

	const form = useForm({
		resolver: valibotResolver(schema),
		defaultValues: {
			email: email || "",
			password: "",
		},
	});

	async function onSubmit(data: v.InferInput<typeof schema>) {
		setIsLoading(true);
		try {
			const { success, error } = await signIn(data.email, data.password);
			if (error) throw ErrorEvent;
			toast("User successfully logged in", {
				icon: <UserIcon className="w-4 h-4" />,
				style: {
					background: "green",
					color: "white",
				},
			});
			// Check if we're in a dialog or on a page and handle appropriately
			if (
				typeof window !== "undefined" &&
				window.location.pathname.includes("/auth/")
			) {
				// We're on a dedicated auth page, redirect to home
				window.location.replace("/");
			} else {
				// We're likely in a dialog/modal, close it by reloading
				window.location.reload();
			}
		} catch (error: unknown) {
			toast("Error logging in. Please try again", {
				icon: <UserIcon className="w-4 h-4" />,
				style: {
					background: "red",
					color: "white",
				},
			});
		} finally {
			setIsLoading(false);
		}
	}

	return (
		<Card className={cn("bg-transparent border-none shadow-none", className)}>
			<CardHeader className="items-center justify-center text-center space-y-4">
				<ZeekMapsIcon className="w-16 h-16" />
				<CardTitle className="text-3xl font-extrabold tracking-tighter">
					Sign in to {SITE_NAME}
				</CardTitle>
			</CardHeader>
			<CardContent>
				<form id="form-sign-in" onSubmit={form.handleSubmit(onSubmit)}>
					<FieldGroup>
						{/* Email */}
						<Controller
							name="email"
							control={form.control}
							render={({ field, fieldState }) => (
								<Field data-invalid={fieldState.invalid}>
									<FieldLabel
										htmlFor="form-sign-in-email"
										className="w-full font-bold"
									>
										Email
									</FieldLabel>
									<InputGroup className="rounded-full">
										<InputGroupInput
											id="form-sign-in-email"
											type="email"
											autoComplete="email"
											placeholder="z@example.com"
											aria-invalid={fieldState.invalid}
											{...field}
										/>
										<InputGroupAddon>
											<MailIcon />
										</InputGroupAddon>
									</InputGroup>

									{fieldState.invalid && (
										<FieldError errors={[fieldState.error]} />
									)}
								</Field>
							)}
						/>

						{/* Password */}
						<Controller
							name="password"
							control={form.control}
							render={({ field, fieldState }) => (
								<Field data-invalid={fieldState.invalid}>
									<FieldLabel
										htmlFor="form-sign-in-password"
										className="w-full font-bold"
									>
										Password
									</FieldLabel>
									<InputGroup className="rounded-full">
										<InputGroupInput
											id="form-sign-in-password"
											type="password"
											autoComplete="password"
											placeholder="******"
											aria-invalid={fieldState.invalid}
											{...field}
										/>
										<InputGroupAddon>
											<KeyIcon />
										</InputGroupAddon>
									</InputGroup>

									{fieldState.invalid && (
										<FieldError errors={[fieldState.error]} />
									)}
								</Field>
							)}
						/>
					</FieldGroup>
				</form>
			</CardContent>

			<CardFooter>
				<div className="w-full flex flex-col space-y-2">
					<Button
						size="default"
						type="submit"
						className="w-full rounded-full"
						form="form-sign-in"
						disabled={isLoading}
					>
						{isLoading ? "Signing in..." : "Sign in"}
					</Button>
					<p className="text-center text-sm">
						Not a member?{" "}
						<Link
							href="/auth/sign-up"
							className="font-semibold text-md text-link hover:underline hover:underline-offset-4 visited:text-link-visited"
						>
							Sign up
						</Link>
					</p>
				</div>
			</CardFooter>
		</Card>
	);
}
